import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import {
  applyTheme,
  DARK_QUERY,
  readPreference,
  resolveTheme,
  type ThemePreference,
  writePreference,
} from "./theme";
import { ThemeContext, type ThemeState } from "./theme-context";

function safeStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readPreference(safeStorage()),
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia(DARK_QUERY).matches,
  );

  // Follow the OS setting live, so "system" tracks a change without a reload.
  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => {
      setSystemPrefersDark(event.matches);
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, []);

  const resolved = resolveTheme(preference, systemPrefersDark);

  useEffect(() => {
    applyTheme(document.documentElement, resolved);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    writePreference(safeStorage(), next);
    setPreferenceState(next);
  }, []);

  const state = useMemo<ThemeState>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  return <ThemeContext value={state}>{children}</ThemeContext>;
}
